import { useState, useMemo } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { OverflowTip } from '../Hover/OverflowTip'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { Pressable } from '../Pressable'
import type { HelperAssignedItem } from '../../../services/helper/helperAssignedItemsService'
import { getChecklistBookName } from '../../../utils/checklistBooks'
import { useNls } from '../../../nls/nlsProvider'

type Props = {
  open: boolean
  items: HelperAssignedItem[]
  onPick: (items: HelperAssignedItem[]) => void
  onOpenTodos?: () => void
  onDismiss: () => void
  title?: string
  actionLabel?: string
  excludeIds?: string[]
}

const dueLabel = (item: HelperAssignedItem) => {
  const due = item.scheduleEndDate ?? item.endDate
  if (!due) return 'No due date'
  const parsed = new Date(due)
  return Number.isNaN(parsed.getTime()) ? 'No due date' : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const FocusModePicker = ({
  open,
  items,
  onPick,
  onOpenTodos,
  onDismiss,
  title = 'Choose tasks to focus on',
  actionLabel = 'Start Focus',
  excludeIds,
}: Props) => {
  const { t } = useNls()
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const availableItems = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return items
    const excluded = new Set(excludeIds)
    return items.filter((item) => !excluded.has(item.id))
  }, [items, excludeIds])

  if (!open) return null

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id],
    )
  }

  const handleConfirm = () => {
    const selected = availableItems.filter((item) => selectedIds.includes(item.id))
    if (selected.length > 0) {
      onPick(selected)
    }
  }

  const selectedCount = selectedIds.filter((id) => availableItems.some((it) => it.id === id)).length

  return (
    <div
      className="fixed inset-0 z-[61] flex items-end bg-[color-mix(in_srgb,var(--bg)_70%,transparent)]"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onDismiss()
      }}
    >
      <section
        className="w-full rounded-t-[22px] border-t border-[var(--border)] bg-[var(--surface-raised)] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border-strong)]" />
        <header className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-[var(--text-strong)]">{title}</h2>
            <p className="text-xs text-[var(--muted)]">Select one or more tasks to queue</p>
          </div>
          <IconButton
            variant="tertiary"
            size="lg"
            label="Close task picker"
            icon={<CloseRoundedIcon fontSize="small" />}
            onClick={onDismiss}
          />
        </header>
        {availableItems.length === 0 ? (
          <div className="grid min-h-52 place-items-center gap-4 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--surface-card)] p-6 text-center">
            <p className="max-w-xs text-sm font-semibold leading-6 text-[var(--text)]">
              {items.length > 0
                ? 'All available tasks are already active or in your queue.'
                : 'No active tasks to focus on. Assign or pick one from Todos first.'}
            </p>
            {onOpenTodos && items.length === 0 ? (
              <Button
                variant="primary"
                size="md"
                onClick={onOpenTodos}
              >
                Open Todos
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="max-h-[50svh] space-y-2 overflow-y-auto pb-3">
              {availableItems.map((item) => {
                const isSelected = selectedIds.includes(item.id)
                return (
                  <Pressable
                    key={item.id}
                    role="checkbox"
                    aria-checked={isSelected}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${
                      isSelected
                        ? 'border-[color-mix(in_srgb,var(--accent)_70%,transparent)] bg-[var(--accent-soft)]'
                        : 'border-[var(--border)] bg-[var(--surface-card)]'
                    }`}
                    onClick={() => toggleSelect(item.id)}
                  >
                    <div
                      className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors ${
                        isSelected
                          ? 'border-[var(--accent)] bg-[var(--accent)] text-[var(--on-accent)]'
                          : 'border-[var(--border-strong)] bg-[var(--surface)] text-transparent'
                      }`}
                      aria-hidden="true"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2 6 5 9 10 3" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <OverflowTip label={item.title}>
                        <strong className="block truncate text-sm text-[var(--text-strong)]">{item.title}</strong>
                      </OverflowTip>
                      <span className="mt-1 block text-xs text-[var(--muted)]">
                        {t('NLSFocusModePickerDueLabel', { due: dueLabel(item) })} · {getChecklistBookName(item)}
                      </span>
                    </div>
                  </Pressable>
                )
              })}
            </div>
            <div className="pt-2">
              <Button
                variant="primary"
                size="lg"
                disabled={selectedCount === 0}
                className="w-full"
                onClick={handleConfirm}
              >
                {selectedCount === 0
                  ? 'Select tasks to continue'
                  : `${actionLabel} (${selectedCount} task${selectedCount > 1 ? 's' : ''})`}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

